/**
 * ProductFilters Component
 * Filter controls for product list
 */

import React from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { IProductFilters, ProductType, APPLICABLE_PROJECT_TYPES } from '../types/AdminTypes';

interface ProductFiltersProps {
    filters: IProductFilters;
    onFiltersChange: (filters: IProductFilters) => void;
    productType: ProductType;
    families: string[];
    categories: string[];
}

export const ProductFilters: React.FC<ProductFiltersProps> = ({ filters, onFiltersChange, productType, families, categories }) => {
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onFiltersChange({ ...filters, search: event.target.value });
    };

    const handleFamilyChange = (event: SelectChangeEvent) => {
        onFiltersChange({ ...filters, family: event.target.value || undefined });
    };

    const handleCategoryChange = (event: SelectChangeEvent) => {
        onFiltersChange({ ...filters, category: event.target.value || undefined });
    };

    const handleProjectTypeChange = (event: SelectChangeEvent) => {
        onFiltersChange({ ...filters, projectType: event.target.value || undefined });
    };

    const filterControlSx = {
        minWidth: 150,
        '& .MuiOutlinedInput-root': {
            height: 40,
        },
        '& .MuiInputLabel-root': {
            lineHeight: '1.2em',
        },
    };

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
            {/* Search */}
            <TextField
                size="small"
                placeholder="Search products..."
                value={filters.search || ''}
                onChange={handleSearchChange}
                sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                        height: 40,
                    },
                    '& input': {
                        border: 'none !important',
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Family filter (geogrid only) */}
            {productType === 'geogrid' && families.length > 0 && (
                <FormControl size="small" sx={filterControlSx}>
                    <InputLabel>Family</InputLabel>
                    <Select value={filters.family || ''} label="Family" onChange={handleFamilyChange}>
                        <MenuItem value="">
                            <em>All Families</em>
                        </MenuItem>
                        {families.map(family => (
                            <MenuItem key={family} value={family}>
                                {family}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Category filter */}
            <FormControl size="small" sx={{ ...filterControlSx, minWidth: 130 }}>
                <InputLabel>Category</InputLabel>
                <Select value={filters.category || ''} label="Category" onChange={handleCategoryChange}>
                    <MenuItem value="">
                        <em>All Categories</em>
                    </MenuItem>
                    {productType === 'geogrid' ? (
                        <>
                            <MenuItem value="GEOGRID">GEOGRID</MenuItem>
                            <MenuItem value="GEOTEXTILE">GEOTEXTILE</MenuItem>
                        </>
                    ) : (
                        categories.map(category => (
                            <MenuItem key={category} value={category}>
                                {category}
                            </MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>

            {/* Project Type filter (geogrid only) */}
            {productType === 'geogrid' && (
                <FormControl size="small" sx={{ ...filterControlSx, minWidth: 140 }}>
                    <InputLabel>Project Type</InputLabel>
                    <Select value={filters.projectType || ''} label="Project Type" onChange={handleProjectTypeChange}>
                        <MenuItem value="">
                            <em>All Projects</em>
                        </MenuItem>
                        {APPLICABLE_PROJECT_TYPES.map(project => (
                            <MenuItem key={project} value={project}>
                                {project.replace('_', ' ').toUpperCase()}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
};
